import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHero } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, ShieldAlert, GraduationCap, FileWarning, Star, BellRing, ChevronRight, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";

const TYPE = { grievance: "Grievance", disciplinary: "Disciplinary", absence: "Absence", other: "Other" };

function BulkPublish() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    try {
      const c = await api.get("/reward/statements/publishable");
      if (!c.data.count) { toast.info("Every statement is already published."); return; }
      if (!window.confirm(`Publish Total Reward statements for ${c.data.count} employee(s)? They'll be able to see them straight away.`)) return;
      setBusy(true);
      const r = await api.post("/reward/statements/bulk-publish");
      toast.success(`Published ${r.data.published} statement${r.data.published !== 1 ? "s" : ""}.`);
    } catch { toast.error("Could not publish statements"); }
    finally { setBusy(false); }
  };
  return <Button className="bg-white text-[#0B1A2F] hover:bg-slate-100" onClick={run} disabled={busy} data-testid="bulk-publish-btn"><Send size={15} className="mr-2" />{busy ? "Publishing…" : "Publish all statements"}</Button>;
}

// Clickable KPI tile — opens a popup with the underlying list.
const Tile = ({ label, value, accent, icon: Icon, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="text-left bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3 hover:shadow-md hover:border-[#2EA3FD]/50 transition-shadow focus:outline-none focus:ring-2 focus:ring-[#2EA3FD]/40"
  >
    <span className="h-10 w-10 shrink-0 rounded-lg bg-[#EFF8FF] text-[#2EA3FD] grid place-items-center"><Icon size={18} /></span>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">{label}<ChevronRight size={12} className="text-slate-300" /></p>
      <p className={`text-2xl font-extrabold ${accent || "text-[#0B1A2F]"}`}>{value}</p>
    </div>
  </button>
);

// A single row inside a popup — becomes a link when the record points to an employee.
const Row = ({ to, primary, secondary, right, testid }) => {
  const inner = (
    <>
      <div className="min-w-0"><span className="font-medium text-[#0B1A2F]">{primary}</span>{secondary && <p className="text-xs text-slate-500 truncate">{secondary}</p>}</div>
      {right}
    </>
  );
  const cls = "flex items-center justify-between gap-2 px-5 py-3 text-sm";
  return to
    ? <Link to={to} className={`${cls} hover:bg-slate-50`} data-testid={testid}>{inner}</Link>
    : <div className={cls} data-testid={testid}>{inner}</div>;
};

export default function HRHub() {
  const { user } = useAuth();
  const isRewardAdmin = user?.role === "admin";
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null); // one of the tile keys
  useEffect(() => { api.get("/reports/hr-hub").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  const chase = async (id) => { try { const r = await api.post(`/policies/${id}/remind`); toast.success(r.data.message); } catch { toast.error("Couldn't send reminders"); } };
  if (!d) return <div className="text-slate-400">Loading HR hub…</div>;
  const o = d.overview;

  const POPUPS = {
    upcoming_reviews: {
      title: "Upcoming reviews", icon: Star, items: o.upcoming_reviews, report: "appraisals",
      empty: "No reviews scheduled.",
      row: (r, i) => <Row key={i} testid={`popup-review-${i}`} to={r.employee_id ? `/employees/${r.employee_id}?tab=performance` : null}
        primary={r.employee_name} secondary={r.title} right={<span className="text-xs text-slate-400 shrink-0">{r.date ? fmtDate(r.date) : ""}</span>} />,
    },
    probation: {
      title: "Probation reviews", icon: CalendarClock, items: o.probation, report: "probation-reviews",
      empty: "No probation reviews in progress.",
      row: (r, i) => <Row key={i} testid={`popup-probation-${i}`} to={r.employee_id ? `/employees/${r.employee_id}?tab=performance` : null}
        primary={r.employee_name} secondary={r.title} right={<span className="text-xs text-amber-600 shrink-0">{r.date ? fmtDate(r.date) : ""}</span>} />,
    },
    training_renewals: {
      title: "Training renewals (60 days)", icon: GraduationCap, items: o.training_renewals, report: "training-qualifications",
      empty: "Nothing expiring soon.",
      row: (t, i) => <Row key={i} testid={`popup-training-${i}`} to={t.employee_id ? `/employees/${t.employee_id}?tab=training` : null}
        primary={t.employee_name} secondary={t.course} right={<span className="text-xs text-amber-600 shrink-0">{fmtDate(t.expiry_date)}</span>} />,
    },
    open_cases: {
      title: "Open HR cases", icon: ShieldAlert, items: d.open_cases, report: "hr-case-overview",
      empty: "No open cases.",
      row: (c) => <Row key={c.id} testid={`popup-case-${c.id}`} to={c.employee_id ? `/employees/${c.employee_id}?tab=cases` : null}
        primary={c.employee_name} secondary={`${TYPE[c.type] || c.type} · ${c.title}`}
        right={<span className="text-[10px] rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 shrink-0">{(c.status || "").replace("_", " ")}</span>} />,
    },
  };
  const active = open ? POPUPS[open] : null;

  return (
    <div className="space-y-6">
      <PageHero title="HR Hub" subtitle="Everything that needs attention across your people — click any tile for the detail." icon={ShieldAlert} tone="teal" pageKey="hr-hub">
        {isRewardAdmin && <BulkPublish />}
      </PageHero>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile testid="hub-tile-upcoming-reviews" label="Upcoming reviews" value={o.totals.upcoming_reviews} icon={Star} onClick={() => setOpen("upcoming_reviews")} />
        <Tile testid="hub-tile-probation-reviews" label="Probation reviews" value={o.totals.probation} accent="text-amber-600" icon={CalendarClock} onClick={() => setOpen("probation")} />
        <Tile testid="hub-tile-training-renewals" label="Training renewals" value={o.totals.training_renewals} accent="text-amber-600" icon={GraduationCap} onClick={() => setOpen("training_renewals")} />
        <Tile testid="hub-tile-open-hr-cases" label="Open HR cases" value={o.totals.open_cases} accent={o.totals.open_cases ? "text-rose-600" : "text-[#0B1A2F]"} icon={ShieldAlert} onClick={() => setOpen("open_cases")} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="hub-policy-gaps">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><FileWarning size={16} className="text-[#2EA3FD]" /><h3 className="font-semibold text-[#0B1A2F] text-sm">Policy acknowledgement gaps</h3></div>
        <div className="divide-y divide-slate-50 max-h-[24rem] overflow-y-auto">
          {d.policy_gaps.length === 0 ? <p className="text-sm text-slate-400 p-5">No policies requiring acknowledgement.</p> :
            d.policy_gaps.map((p) => (
              <div key={p.policy_id} className="px-5 py-2.5 text-sm" data-testid={`hub-gap-${p.policy_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[#0B1A2F] truncate">{p.title}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] rounded-full px-2 py-0.5 ${p.pending ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{p.pending ? `${p.pending} pending` : "All acknowledged"}</span>
                    {p.pending > 0 && <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => chase(p.policy_id)} data-testid={`hub-chase-${p.policy_id}`}><BellRing size={12} className="mr-1" />Chase</Button>}
                  </div>
                </div>
                <p className="text-xs text-slate-500">{p.eligible - p.pending}/{p.eligible} acknowledged{p.pending_names.length ? ` · waiting on ${p.pending_names.slice(0, 3).join(", ")}${p.pending_names.length > 3 ? "…" : ""}` : ""}</p>
              </div>
            ))}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto p-0" data-testid="hub-popup">
          {active && (
            <>
              <DialogHeader className="px-5 pt-5">
                <DialogTitle className="flex items-center gap-2"><active.icon size={16} className="text-[#2EA3FD]" />{active.title}</DialogTitle>
              </DialogHeader>
              <div className="divide-y divide-slate-50">
                {(active.items || []).length === 0
                  ? <p className="text-sm text-slate-400 p-6">{active.empty}</p>
                  : active.items.map((it, i) => active.row(it, i))}
              </div>
              {active.report && (
                <Link to={`/reports/${active.report}`} className="flex items-center justify-center gap-1.5 px-5 py-3 text-sm font-semibold text-[#2EA3FD] hover:bg-slate-50 border-t border-slate-100" data-testid="popup-open-report">
                  Open full report<ArrowRight size={14} />
                </Link>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
